/**
 * Canonical theme keys, shared between backend (validation of the stored
 * `user.theme_preference` + company `metadata.defaultTheme`) and frontend
 * (the picker options, font map, and `applyLexTheme`).
 *
 * Two products, two theme families:
 *   - Lexicanon ships 8 selectable [data-theme] looks (Pastel Calm is the
 *     global default; `classic` is the attribute-less teal-on-white baseline).
 *   - Voxcanon keeps its dual surface (fantasy / scifi).
 *
 * One DB column (`user.theme_preference`) stores either family — a user is
 * only ever on one product, so the value is interpreted against that product.
 */

export const LEX_THEME_KEYS = [
	"pastel",
	"editorial",
	"signal",
	"ink",
	"warm",
	"evergreen",
	"bordeaux",
	"classic",
] as const

export type TLexThemeKey = (typeof LEX_THEME_KEYS)[number]

/** Pastel Calm — the global default since 2026-05-30. */
export const LEX_DEFAULT_THEME: TLexThemeKey = "pastel"

/** Human labels for the picker UIs. Not translated — they're proper nouns. */
export const LEX_THEME_LABELS: Record<TLexThemeKey, string> = {
	pastel: "Pastel Calm",
	editorial: "Editorial Modern",
	signal: "Clean Signal",
	ink: "Ink",
	warm: "Warm Minimal",
	evergreen: "Evergreen",
	bordeaux: "Bordeaux & Cream",
	classic: "Classic",
}

export const VOX_THEME_KEYS = ["fantasy", "scifi"] as const
export type TVoxThemeKey = (typeof VOX_THEME_KEYS)[number]

/** Every value the shared `theme_preference` column may legitimately hold. */
export const ALL_THEME_KEYS = [...LEX_THEME_KEYS, ...VOX_THEME_KEYS] as const
export type TThemeKey = (typeof ALL_THEME_KEYS)[number]

export function isLexThemeKey(value: unknown): value is TLexThemeKey {
	return typeof value === "string" && (LEX_THEME_KEYS as readonly string[]).includes(value)
}

export function isVoxThemeKey(value: unknown): value is TVoxThemeKey {
	return typeof value === "string" && (VOX_THEME_KEYS as readonly string[]).includes(value)
}

export function isThemeKey(value: unknown): value is TThemeKey {
	return isLexThemeKey(value) || isVoxThemeKey(value)
}

/** Coerce an arbitrary stored value to a Lexicanon key, or null if it isn't one. */
export function asLexThemeKey(value: unknown): TLexThemeKey | null {
	return isLexThemeKey(value) ? value : null
}
